import { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useFeatureFlags } from '../context/FeatureFlagsContext';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import NotificationBell from '../components/NotificationBell';
import OnboardingModal from '../components/OnboardingModal';
import RemoteSupportButton from '../components/RemoteSupportButton';
import { 
  LayoutDashboard, 
  Ticket, 
  FileText, 
  FileCheck, 
  User,
  LogOut,
  Menu,
  X,
  ChevronDown,
  ChevronRight,
  Globe,
  MessageSquare,
  GitBranch,
  Users,
  MapPin,
  ClipboardList,
  FolderOpen,
  HelpCircle,
  Network,
  BookMarked,
  Package,
  Bell,
  Mail,
  Scale,
  DollarSign,
  BookOpen,
  Clock,
  Receipt,
  Headphones,
  CreditCard,
  ServerCog,
  Server,
  FileStack,
  Wrench,
  ClipboardCheck,
  Activity,
  Zap,
  Map,
} from 'lucide-react';

const LOGO_ICON = "/tewatati-logo.png";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DashboardLayout = () => {
  const { user, logout, refreshUser } = useAuth();
  const { t, language, toggleLanguage } = useLanguage();
  const { isEnabled: ff } = useFeatureFlags();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingChecked, setOnboardingChecked] = useState(false);
  const [isTewatatiMember, setIsTewatatiMember] = useState(false);

  // Determine Tewatati membership
  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tewatati/me`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (r.ok) {
          const d = await r.json();
          setIsTewatatiMember(!!d.is_tewatati_member);
        }
      } catch { /* swallow */ }
    })();
  }, [user]);

  // Check if onboarding is needed on mount
  useEffect(() => {
    const checkOnboarding = async () => {
      if (!user || onboardingChecked) return;
      
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/api/onboarding/status`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.onboarding_required) {
            setShowOnboarding(true);
          }
        }
      } catch (error) {
        console.error('Error checking onboarding status:', error);
      } finally {
        setOnboardingChecked(true);
      }
    };
    
    checkOnboarding();
  }, [user, onboardingChecked]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
    if (refreshUser) refreshUser();
  };

  // Get user permissions with defaults
  const resolvedPerms = user?.resolved_permissions || {};
  const canView = (mod) => resolvedPerms[mod]?.view !== false;
  const isManager = user?.is_manager === true;

  // Collapsible section state
  const [expandedSections, setExpandedSections] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('portal_nav_sections') || '{}');
    } catch { return {}; }
  });

  const toggleSection = (key) => {
    setExpandedSections(prev => {
      const next = { ...prev, [key]: !prev[key] };
      localStorage.setItem('portal_nav_sections', JSON.stringify(next));
      return next;
    });
  };

  // Auto-expand section containing active route
  useEffect(() => {
    for (const group of navGroups) {
      if (group.items?.some(i => isActive(i.path))) {
        setExpandedSections(prev => ({ ...prev, [group.key]: true }));
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Build grouped navigation
  const navGroups = [
    {
      key: 'main',
      standalone: { path: '/dashboard', icon: LayoutDashboard, labelKey: 'sidebar_dashboard', show: ff('dashboard') },
    },
    {
      key: 'support',
      label: 'Support',
      icon: Headphones,
      items: [
        { path: '/dashboard/tickets', icon: Ticket, labelKey: 'sidebar_tickets', show: canView('tickets') && ff('tickets') },
        { path: '/dashboard/interventions', icon: MapPin, labelKey: 'sidebar_interventions', show: canView('interventions') && ff('interventions') },
        { path: '/dashboard/changes', icon: GitBranch, labelKey: 'sidebar_changes', show: canView('changes') && ff('changes') },
      ].filter(i => i.show),
    },
    {
      key: 'billing',
      label: 'Billing',
      icon: CreditCard,
      items: [
        { path: '/dashboard/invoices', icon: FileText, labelKey: 'sidebar_invoices', show: canView('invoices') && ff('invoices') },
        { path: '/dashboard/proposals', icon: FileCheck, labelKey: 'sidebar_proposals', show: canView('proposals') && ff('proposals') },
        { path: '/dashboard/quotes', icon: DollarSign, labelKey: 'sidebar_quotes', show: canView('quotes') && ff('quotes') },
        { path: '/dashboard/contracts', icon: Scale, labelKey: 'sidebar_contracts', show: canView('contracts') && ff('contracts') },
        { path: '/dashboard/expenses', icon: Receipt, labelKey: 'sidebar_expenses', show: canView('expenses') && ff('expenses') },
      ].filter(i => i.show),
    },
    {
      key: 'resources',
      label: 'Resources',
      icon: ServerCog,
      items: (ff('client_resources') ? [
        { path: '/dashboard/hours', icon: Clock, labelKey: 'sidebar_hours', show: canView('hours') && ff('client_hours') },
        { path: '/dashboard/racks', icon: Server, labelKey: 'sidebar_racks', show: canView('racks') && ff('racks') },
        { path: '/dashboard/maintenance-notices', icon: Wrench, labelKey: 'sidebar_maintenance', show: canView('maintenance_notices') && ff('maintenance_notices') },
        { path: '/dashboard/assessments', icon: ClipboardCheck, labelKey: 'sidebar_assessments', show: canView('assessments') && ff('assessments') },
        { path: '/dashboard/ipam', icon: Network, labelKey: 'sidebar_ipam', show: canView('ipam') && ff('ipam') },
        { path: '/dashboard/assets', icon: Package, labelKey: 'sidebar_assets', show: canView('assets') && ff('assets') },
        { path: '/dashboard/service-catalog', icon: BookOpen, labelKey: 'sidebar_service_catalog', show: canView('service_catalog') && ff('service_catalog') },
        { path: '/dashboard/knowledge-base', icon: BookMarked, labelKey: 'sidebar_kb', show: canView('knowledge_base') && ff('knowledge_base') },
        { path: '/dashboard/faq', icon: HelpCircle, labelKey: 'sidebar_faq', show: canView('faq') && ff('faq') },
      ] : []).filter(i => i.show),
    },
    {
      key: 'tewatati',
      label: 'Tewatati',
      icon: Activity,
      items: [
        { path: '/dashboard/tewatati-status', icon: Activity, labelKey: 'sidebar_tewatati_status', show: isTewatatiMember && ff('tewatati') && ff('tewatati_status_page'), label: 'Status Page' },
        { path: '/dashboard/server-racks', icon: Server, labelKey: 'sidebar_server_racks', label: 'Racks', show: isTewatatiMember && ff('tewatati') && ff('tewatati_server_racks') },
        { path: '/dashboard/tewatati-public-ips', icon: Globe, labelKey: 'sidebar_tewatati_public_ips', label: 'Public IPs', show: isTewatatiMember && ff('tewatati') && ff('tewatati_public_ips') },
        { path: '/dashboard/power-graph', icon: Zap, labelKey: 'sidebar_tewatati_power_graph', label: 'Power Graph', show: isTewatatiMember && ff('tewatati') && ff('tewatati_power_graph') },
        { path: '/dashboard/power-topology', icon: Map, labelKey: 'sidebar_tewatati_power_topology', label: 'Power Topology', show: isTewatatiMember && ff('tewatati') && ff('tewatati_power_topology') },
      ].filter(i => i.show),
    },
    {
      key: 'documents',
      label: 'Documents',
      icon: FileStack,
      items: [
        { path: '/dashboard/files', icon: FolderOpen, labelKey: 'sidebar_files', show: canView('files') },
        { path: '/dashboard/onboarding', icon: ClipboardList, labelKey: 'sidebar_onboarding', show: canView('onboarding') && ff('onboarding') },
      ].filter(i => i.show),
    },
    {
      key: 'communication',
      label: 'Communication',
      icon: MessageSquare,
      items: [
        { path: '/dashboard/messages', icon: MessageSquare, labelKey: 'sidebar_messages', show: canView('messages') && ff('client_messages') },
        { path: '/dashboard/emails', icon: Mail, labelKey: 'sidebar_emails', show: canView('emails') },
        { path: '/dashboard/notifications', icon: Bell, labelKey: 'sidebar_notifications', show: canView('notifications') },
      ].filter(i => i.show),
    },
    {
      key: 'account',
      label: 'Account',
      icon: User,
      items: [
        { path: '/dashboard/team', icon: Users, labelKey: 'sidebar_team', show: isManager && canView('team') },
        { path: '/dashboard/account', icon: User, labelKey: 'sidebar_account', show: true },
      ].filter(i => i.show),
    },
  ].filter(g => g.standalone?.show || (g.items && g.items.length > 0));

  const isActive = (path) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(path);
  };

  const getAllNavItems = () => {
    const items = [];
    for (const g of navGroups) {
      if (g.standalone) items.push(g.standalone);
      if (g.items) items.push(...g.items);
    }
    return items;
  };

  const getCurrentPageTitle = () => {
    const item = getAllNavItems().find(item => isActive(item.path));
    return item ? (item.label || t(item.labelKey)) : "Dashboard";
  };

  const renderNavLink = (item, onClick) => (
    <Link
      key={item.path}
      to={item.path}
      onClick={onClick}
      className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors text-sm ${
        isActive(item.path)
          ? 'bg-blue-50 text-[#7B1F2A] border border-blue-100 font-semibold'
          : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
      }`}
      data-testid={`nav-${item.labelKey.replace('sidebar_', '')}`}
    >
      <item.icon className="h-4 w-4" />
      <span>{item.label || t(item.labelKey)}</span>
    </Link>
  );

  const renderNavGroup = (group, onClick) => {
    if (group.standalone) return renderNavLink(group.standalone, onClick);
    const isExpanded = expandedSections[group.key] !== false;
    const hasActive = group.items.some(i => isActive(i.path));
    return (
      <div key={group.key} data-testid={`nav-group-${group.key}`}>
        <button
          onClick={() => toggleSection(group.key)}
          className={`w-full flex items-center justify-between px-4 py-2.5 rounded-lg text-sm transition-colors ${
            hasActive ? 'text-[#7B1F2A] font-semibold' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
          }`}
        >
          <div className="flex items-center gap-3">
            <group.icon className="h-4 w-4" />
            <span>{group.label}</span>
          </div>
          {isExpanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        </button>
        {isExpanded && (
          <div className="ml-4 border-l border-slate-100 pl-2 space-y-0.5 mt-0.5">
            {group.items.map(item => renderNavLink(item, onClick))}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Onboarding Modal */}
      <OnboardingModal 
        isOpen={showOnboarding}
        onClose={() => setShowOnboarding(false)}
        onComplete={handleOnboardingComplete}
      />
      
      <div className="min-h-screen bg-slate-50 flex">
      {/* Sidebar - Desktop */}
      <aside className="hidden lg:flex flex-col w-72 fixed inset-y-0 left-0 z-30 bg-white border-r border-slate-200">
        {/* Logo */}
        <div className="h-28 flex items-center gap-3 px-4 border-b border-slate-200">
          <img src={LOGO_ICON} alt="Tewatati" className="h-20 w-auto shrink-0" />
          <div className="leading-tight">
            <div className="text-lg font-bold text-[#3d0f17] font-['Sora']">Tewatati</div>
            <div className="text-sm font-semibold text-[#7B1F2A] font-['Sora'] -mt-0.5">Portal</div>
          </div>
        </div>
        
        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto px-4 py-6 space-y-1">
          {navGroups.map(group => renderNavGroup(group))}
        </nav>
        
        {/* Remote Support Button */}
        <div className="px-4 pb-4">
          <RemoteSupportButton variant="sidebar" language={language} context="client" />
        </div>
        
        {/* User Section */}
        <div className="p-4 border-t border-slate-200">
          <div className="p-3 rounded-lg bg-slate-50">
            <div className="text-sm font-medium text-slate-900 truncate">{user?.full_name}</div>
            <div className="text-xs text-slate-500 truncate">{user?.company_name}</div>
          </div>
        </div>
      </aside>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/30 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - Mobile */}
      <aside className={`lg:hidden fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-slate-200 transform transition-transform duration-300 ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        {/* Logo */}
        <div className="h-24 flex items-center justify-between px-4 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <img src={LOGO_ICON} alt="Tewatati" className="h-14 w-auto shrink-0" />
            <div className="leading-tight">
              <div className="text-base font-bold text-[#3d0f17] font-['Sora']">Tewatati</div>
              <div className="text-xs font-semibold text-[#7B1F2A] font-['Sora'] -mt-0.5">Portal</div>
            </div>
          </div>
          <button onClick={() => setSidebarOpen(false)} className="text-slate-400">
            <X className="h-5 w-5" />
          </button>
        </div>
        
        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto px-4 py-6 space-y-1">
          {navGroups.map(group => renderNavGroup(group, () => setSidebarOpen(false)))}
        </nav>
        
        {/* User Section */}
        <div className="p-4 border-t border-slate-200">
          <div className="p-3 rounded-lg bg-slate-50">
            <div className="text-sm font-medium text-slate-900 truncate">{user?.full_name}</div>
            <div className="text-xs text-slate-500 truncate">{user?.company_name}</div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 lg:ml-72 flex flex-col min-h-screen">
        {/* Top Bar */}
        <header className="h-16 flex items-center justify-between px-4 sm:px-6 border-b border-slate-200 bg-white sticky top-0 z-20">
          {/* Mobile Menu Button */}
          <button 
            className="lg:hidden text-slate-500 hover:text-slate-900"
            onClick={() => setSidebarOpen(true)}
            data-testid="mobile-sidebar-btn"
          >
            <Menu className="h-6 w-6" />
          </button>
          
          {/* Page Title - Hidden on mobile */}
          <div className="hidden lg:block text-lg font-semibold text-slate-900 font-['Sora']">
            {getCurrentPageTitle()}
          </div>
          
          {/* Right Side */}
          <div className="flex items-center gap-4">
            {/* Notifications */}
            <NotificationBell />
            
            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2 text-slate-700" data-testid="user-menu-btn">
                  <div className="w-8 h-8 rounded-full bg-[#7B1F2A] flex items-center justify-center text-white font-medium">
                    {user?.full_name?.charAt(0) || 'U'}
                  </div>
                  <span className="hidden sm:inline">{user?.full_name}</span>
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-white border-slate-200">
                <div className="px-3 py-2">
                  <div className="text-sm font-medium text-slate-900">{user?.full_name}</div>
                  <div className="text-xs text-slate-500">{user?.email}</div>
                </div>
                <DropdownMenuSeparator className="bg-slate-200" />
                <DropdownMenuItem asChild>
                  <Link to="/dashboard/account" className="cursor-pointer">
                    <User className="mr-2 h-4 w-4" />
                    {"Account Settings"}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-slate-200" />
                <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-red-600 focus:text-red-600" data-testid="logout-btn">
                  <LogOut className="mr-2 h-4 w-4" />
                  {"Sign out"}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        
        {/* Main Content Area */}
        <main className="flex-1 p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
    </>
  );
};

export default DashboardLayout;
